import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  fetchCoaches,
  addRhCampaignInvoice,
  importRhCampaignInvoicesExcel,
  downloadRhImportTemplate,
} from '../../api/index'
import {
  FISCAL_CATEGORY_OPTIONS,
  formatFiscalCategoryLabel,
  isAutoEntrepreneur,
  normalizeFiscalCategory,
} from '../../utils/fiscalCategory'
import { formatTND } from '../../utils/formatTND'
import { normalizeCoachRetenue } from '../../utils/retenue'
import styles from './CampagneDetail.module.css'

const DEFAULT_SUPPLEMENT_FISCAL = 'auto_entrepreneur'

const DROIT_TIMBRE = 1
const DEFAULT_TVA = 0.19

const parseAmountInput = (value) => Number(String(value).replace(',', '.'))

/** Même formule que le serveur (import Sage / compléter campagne). */
const estimateInvoiceNetAPayer = (honorairesHT, coach) => {
  const ht = Number(honorairesHT)
  if (Number.isNaN(ht) || ht <= 0) return null
  if (isAutoEntrepreneur(coach)) return Math.round((ht + DROIT_TIMBRE) * 1000) / 1000
  const retenue = normalizeCoachRetenue(coach.retenue, coach.fiscalCategory)
  const netHorsTimbre = Math.round(ht * (1 + DEFAULT_TVA) * (1 - retenue) * 1000) / 1000
  return Math.round((netHorsTimbre + DROIT_TIMBRE) * 1000) / 1000
}

const getSupplementAmountCopy = (coach) => {
  if (!coach || isAutoEntrepreneur(coach)) {
    return {
      label: 'Montant Sage (net à payer, hors timbre)',
      fieldHint:
        'Colonne « Net à payer » du fichier Sage : montant sans le dinar de timbre. Sur la facture : honoraires + 1 TND timbre = net à payer.',
    }
  }
  return {
    label: 'Montant Sage (honoraires HT)',
    fieldHint:
      'Montant HT (comme sur le modèle régime réel / forfaitaire), pas le net après TVA/retenue. La facture affichera TVA 19 %, retenue à la source, puis +1 TND de timbre.',
  }
}

const triggerBlobDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  window.URL.revokeObjectURL(url)
}

const CampaignSupplementModal = ({
  open,
  campaignId,
  existingCoachIds = [],
  onClose,
  onSuccess,
}) => {
  const [mode, setMode] = useState('manual')
  const [search, setSearch] = useState('')
  const [fiscalFilter, setFiscalFilter] = useState(DEFAULT_SUPPLEMENT_FISCAL)
  const [coaches, setCoaches] = useState([])
  const [searching, setSearching] = useState(false)
  const [selectedCoach, setSelectedCoach] = useState(null)
  const [sageNetAPayer, setSageNetAPayer] = useState('')
  const [sendEmail, setSendEmail] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [excelFile, setExcelFile] = useState(null)
  const [importSummary, setImportSummary] = useState('')

  const existingSet = useMemo(
    () => new Set(existingCoachIds.map(String)),
    [existingCoachIds]
  )

  const eligibleCoaches = useMemo(
    () => coaches.filter((coach) => !existingSet.has(String(coach.id || coach._id))),
    [coaches, existingSet]
  )

  const loadCoaches = useCallback(async (query, fiscal = fiscalFilter) => {
    setSearching(true)
    try {
      const { data } = await fetchCoaches({
        search: query,
        page: 1,
        limit: 50,
        status: 'active',
        fiscal,
        excludeIds: [...existingSet].join(','),
      })
      setCoaches(data.coaches || [])
    } catch {
      setCoaches([])
    } finally {
      setSearching(false)
    }
  }, [existingSet, fiscalFilter])

  useEffect(() => {
    if (!open) return undefined
    setError('')
    setImportSummary('')
    setSelectedCoach(null)
    setSageNetAPayer('')
    setSendEmail(true)
    setSearch('')
    setFiscalFilter(DEFAULT_SUPPLEMENT_FISCAL)
    setExcelFile(null)
    setMode('manual')
    loadCoaches('', DEFAULT_SUPPLEMENT_FISCAL)
    return undefined
  }, [open, loadCoaches])

  useEffect(() => {
    if (!open || mode !== 'manual') return undefined
    const timer = window.setTimeout(() => loadCoaches(search.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [open, mode, search, fiscalFilter, loadCoaches])

  useEffect(() => {
    if (!selectedCoach) return
    if (normalizeFiscalCategory(selectedCoach.fiscalCategory) !== fiscalFilter) {
      setSelectedCoach(null)
      setSageNetAPayer('')
    }
  }, [fiscalFilter, selectedCoach])

  const handleDownloadTemplate = async () => {
    setError('')
    try {
      const { data: blob } = await downloadRhImportTemplate({ type: 'empty' })
      triggerBlobDownload(blob, 'modele-complement-campagne.xlsx')
    } catch (err) {
      setError(err?.response?.data?.message || 'Impossible de télécharger le modèle')
    }
  }

  const handleSubmitManual = async (e) => {
    e.preventDefault()
    if (!selectedCoach) {
      setError('Choisissez un coach')
      return
    }
    const amount = Number(String(sageNetAPayer).replace(',', '.'))
    if (Number.isNaN(amount) || amount <= 0) {
      setError(
        isAutoEntrepreneur(selectedCoach)
          ? 'Indiquez le net Sage (hors timbre) > 0'
          : 'Indiquez les honoraires HT > 0'
      )
      return
    }

    setSubmitting(true)
    setError('')
    try {
      const { data } = await addRhCampaignInvoice(campaignId, {
        coachId: selectedCoach.id || selectedCoach._id,
        sageNetAPayer: amount,
        sendEmail,
      })
      onSuccess?.(data)
      onClose()
    } catch (err) {
      setError(err?.response?.data?.message || 'Impossible d\'ajouter la facture')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmitExcel = async (e) => {
    e.preventDefault()
    if (!excelFile) {
      setError('Choisissez un fichier Excel')
      return
    }

    setSubmitting(true)
    setError('')
    setImportSummary('')
    try {
      const formData = new FormData()
      formData.append('file', excelFile)
      formData.append('sendEmail', sendEmail ? '1' : '0')
      const { data } = await importRhCampaignInvoicesExcel(campaignId, formData)
      const parts = [data.message]
      if (data.failureCount > 0) {
        parts.push(`${data.failureCount} ligne(s) en erreur`)
      }
      setImportSummary(parts.join(' · '))
      onSuccess?.(data)
      if (data.failureCount === 0) {
        onClose()
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Import impossible')
    } finally {
      setSubmitting(false)
    }
  }

  const amountCopy = getSupplementAmountCopy(selectedCoach)
  const parsedAmount = sageNetAPayer ? parseAmountInput(sageNetAPayer) : NaN
  const estimatedNet = selectedCoach && !Number.isNaN(parsedAmount) && parsedAmount > 0
    ? estimateInvoiceNetAPayer(parsedAmount, selectedCoach)
    : null

  if (!open) return null

  return (
    <div className={styles.supplementOverlay} role="presentation" onClick={onClose}>
      <div
        className={styles.supplementDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="supplement-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <h2 id="supplement-title">Compléter la campagne</h2>
        <p className={styles.supplementHint}>
          Coaches oubliés à l&apos;import : mêmes règles que l&apos;import mensuel.
          {' '}
          <strong>Auto-entrepreneur</strong> : colonne Sage « Net à payer » (hors timbre).
          {' '}
          <strong>Régime réel / forfaitaire</strong> : honoraires HT.
          Numéros de facture = suite du mois ; +1 TND de timbre sur chaque facture.
        </p>

        <div className={styles.supplementTabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'manual'}
            className={mode === 'manual' ? styles.supplementTabActive : styles.supplementTab}
            onClick={() => { setMode('manual'); setError('') }}
          >
            1 coach
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'excel'}
            className={mode === 'excel' ? styles.supplementTabActive : styles.supplementTab}
            onClick={() => { setMode('excel'); setError('') }}
          >
            Fichier Excel (1 à N)
          </button>
        </div>

        {mode === 'manual' ? (
          <form onSubmit={handleSubmitManual}>
            <div className={styles.supplementFieldRow}>
              <div className={styles.supplementField}>
                <label htmlFor="supplement-search">Rechercher un coach</label>
                <input
                  id="supplement-search"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Nom, email, matricule fiscal, Sage…"
                  autoComplete="off"
                />
              </div>
              <div className={styles.supplementField}>
                <label htmlFor="supplement-fiscal-filter">Catégorie fiscale</label>
                <select
                  id="supplement-fiscal-filter"
                  className={styles.filterSelect}
                  value={fiscalFilter}
                  onChange={(e) => setFiscalFilter(e.target.value)}
                >
                  {FISCAL_CATEGORY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.supplementCoachList} aria-busy={searching}>
              {eligibleCoaches.length === 0 && !searching && (
                <p className={styles.supplementEmpty}>
                  {search.trim()
                    ? `Aucun coach actif (${formatFiscalCategoryLabel(fiscalFilter)}) hors campagne pour cette recherche.`
                    : `Aucun coach actif (${formatFiscalCategoryLabel(fiscalFilter)}) à ajouter — déjà facturés ou liste vide.`}
                </p>
              )}
              {eligibleCoaches.map((coach) => {
                const coachKey = String(coach.id || coach._id)
                const isSelected = selectedCoach && String(selectedCoach.id || selectedCoach._id) === coachKey
                return (
                  <button
                    key={coachKey}
                    type="button"
                    className={`${styles.supplementCoachRow} ${isSelected ? styles.supplementCoachRowSelected : ''}`}
                    onClick={() => setSelectedCoach(coach)}
                  >
                    <span className={styles.supplementCoachName}>{coach.name}</span>
                    <span className={styles.supplementCoachMeta}>
                      {formatFiscalCategoryLabel(coach.fiscalCategory)}
                      {coach.email ? ` · ${coach.email}` : ' · email manquant'}
                    </span>
                  </button>
                )
              })}
            </div>

            <div className={styles.supplementField}>
              <label htmlFor="supplement-amount">{amountCopy.label}</label>
              <input
                id="supplement-amount"
                type="text"
                inputMode="decimal"
                value={sageNetAPayer}
                onChange={(e) => setSageNetAPayer(e.target.value)}
                placeholder="Ex. 252 ou 650,500"
                aria-describedby="supplement-amount-hint"
              />
              <span id="supplement-amount-hint" className={styles.supplementAmountHint}>
                {selectedCoach ? amountCopy.fieldHint : 'Choisissez un coach pour voir quelle colonne reprendre.'}
              </span>
              {estimatedNet != null && (
                <span className={styles.supplementAmountPreview}>
                  Saisi : {formatTND(parsedAmount)} TND
                  {' · '}
                  Net à payer sur facture (estimation) : {formatTND(estimatedNet)} TND
                  {isAutoEntrepreneur(selectedCoach)
                    ? ' (montant saisi + 1 TND timbre)'
                    : ' (HT → TVA → retenue → +1 TND timbre)'}
                </span>
              )}
            </div>

            <label className={styles.supplementCheck}>
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              Envoyer l&apos;email au coach après création
            </label>

            {error && <p className={styles.supplementError} role="alert">{error}</p>}

            <div className={styles.supplementActions}>
              <button type="button" className={styles.btnSecondary} onClick={onClose} disabled={submitting}>
                Annuler
              </button>
              <button type="submit" className={styles.ctaBtn} disabled={submitting || !selectedCoach}>
                {submitting ? 'Création…' : 'Créer la facture'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmitExcel}>
            <p className={styles.supplementHint}>
              Mettez <strong>uniquement</strong> les coaches oubliés (feuille Sage ou régime, comme l&apos;import mensuel).
            </p>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={handleDownloadTemplate}
              disabled={submitting}
            >
              Télécharger le modèle Excel
            </button>

            <div className={styles.supplementField}>
              <label htmlFor="supplement-excel">Fichier Excel</label>
              <input
                id="supplement-excel"
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
              />
              {excelFile && (
                <span className={styles.supplementAmountHint}>{excelFile.name}</span>
              )}
            </div>

            <label className={styles.supplementCheck}>
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              Envoyer un email à chaque coach ajouté
            </label>

            {importSummary && (
              <p className={styles.supplementAmountHint} role="status">{importSummary}</p>
            )}
            {error && <p className={styles.supplementError} role="alert">{error}</p>}

            <div className={styles.supplementActions}>
              <button type="button" className={styles.btnSecondary} onClick={onClose} disabled={submitting}>
                Annuler
              </button>
              <button type="submit" className={styles.ctaBtn} disabled={submitting || !excelFile}>
                {submitting ? 'Import…' : 'Importer et créer les factures'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default CampaignSupplementModal
